import { Component, DestroyRef, inject, OnDestroy } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { parseZone } from 'moment';
import { map, Subscription } from 'rxjs';
import { Ids } from '../../../models/common/ids.model';
import { OrderStatusTag } from '../../../models/common/order-status-tag.model';
import { OrdersListFilters } from '../../../models/common/orders-list-filters.model';
import { CommonService } from '../../../services/common.service';
import { GraphQLService } from '../../../services/graph-ql.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GridColumn, GridColumnTypeEnum } from '../../../models/common/grid-column.models';
import { OrderListItem } from '../../../models/common/order-list-item.model';
import { SidebarTypeEnum } from '../../../models/common/sidebar-type.enum';
import { PhotographerService } from '../../../services/photographer.service';
import { SidenavService } from '../../../shared/services/sidenav.service';
import { TopbarService } from '../../../shared/services/topbar.service';
import { UtilityFunctions } from '../../../shared/utilities/functions.models';
import { PhotographerQueries } from '../../../models/shared/enums/get-query';
import { AppService } from '../../../app.service';

@Component({
  standalone: false,
  selector: 'bt-photographer-order-list-auto-sorting',
  templateUrl: './photographer-order-list-auto-sorting.component.html',
  styleUrls: ['./photographer-order-list-auto-sorting.component.scss']
})
export class PhotographerOrderListAutoSortingComponent implements OnDestroy {
  readonly #graphQLService = inject(GraphQLService);
  readonly #appService = inject(AppService);
  readonly #destroyRef = inject(DestroyRef);

  public currentSidebar: SidebarTypeEnum;
  public sidebarTypeEnum = SidebarTypeEnum;
  public gridColumnTypeEnum = GridColumnTypeEnum;
  public gridColumns: GridColumn[];
  public loaderId = UtilityFunctions.newGuid();

  public orders: { data?: OrderListItem[], page?: number, search?: string, size: number, total?: number } = {
    size: 10
  };
  public statusFilters: { data?: OrderStatusTag[], selectedCached: OrderStatusTag[], selectedCurrent?: OrderStatusTag[], loaderId: string } = {
    selectedCached: [],
    loaderId: UtilityFunctions.newGuid()
  };
  public datesFilters: { fromDateCached?: string, fromDateCurrent?: string, toDateCached?: string, toDateCurrent?: string, notSetDueDate?: boolean, notSetDueDateCurrent?: boolean } = {};

  public orderId: number;
  public orderNumber: string;
  public modelId: number;

  private subscription = new Subscription();

  constructor(
    private _activatedRoute: ActivatedRoute,
    private photoService: PhotographerService,
    private commonService: CommonService,
    private topbarService: TopbarService,
    private sidenavService: SidenavService
  ) {
    this.queryParamsSubscribe()
    this.setGridColumns();
    this.searchTextSubscription();
  }

  public ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  public onOpenFilter() {
    this.currentSidebar = SidebarTypeEnum.filters;
    this.statusFilters.selectedCurrent = UtilityFunctions.DeepClone(this.statusFilters.selectedCached);
    this.datesFilters.fromDateCurrent = UtilityFunctions.DeepClone(this.datesFilters.fromDateCached);
    this.datesFilters.toDateCurrent = UtilityFunctions.DeepClone(this.datesFilters.toDateCached);
    this.datesFilters.notSetDueDateCurrent = this.datesFilters.notSetDueDate;
    this.getOrderStatus();
  }

  public getOrderStatus() {
    this.commonService.getOrderStatus(this.statusFilters.loaderId).subscribe(data => {
      data.forEach(p => p.checked = this.statusFilters.selectedCurrent.some(el => el.orderStatusId === p.orderStatusId));
      this.statusFilters.data = data;
    });
  }

  public applyFilters() {
    this.statusFilters.selectedCached = this.statusFilters.selectedCurrent;
    this.datesFilters.notSetDueDate = this.datesFilters.notSetDueDateCurrent;
    this.datesFilters.fromDateCached = this.datesFilters.notSetDueDate ? null : this.datesFilters.fromDateCurrent;
    this.datesFilters.toDateCached = this.datesFilters.notSetDueDate ? null : this.datesFilters.toDateCurrent;
    this.onExitFilters();
    this.getOrders(0);
  }

  public onExitFilters() {
    this.currentSidebar = null;
    this.statusFilters.data = null;
    this.statusFilters.selectedCurrent = null;
    this.datesFilters.fromDateCurrent = null;
    this.datesFilters.toDateCurrent = null;
  }

  public removeStatusFilter(s: OrderStatusTag) {
    const index = this.statusFilters.selectedCached.findIndex(x => x.orderStatusId === s.orderStatusId);
    this.statusFilters.selectedCached.splice(index, 1);
    this.getOrders(0);
  }

  public clickStatusLabel(s: OrderStatusTag) {
    s.checked = !s.checked;
    this.changeStatusCheckboxValue(s);
  }

  public changeStatusCheckboxValue(s: OrderStatusTag) {
    if (!s.checked) {
      const index = this.statusFilters.selectedCurrent.findIndex(st => st.orderStatusId === s.orderStatusId);
      this.statusFilters.selectedCurrent.splice(index, 1);
    } else {
      this.statusFilters.selectedCurrent.push(s);
    }
  }

  public removeFromDateFilter() {
    this.datesFilters.fromDateCached = null;
    this.getOrders(0);
  }

  public removeToDateFilter() {
    this.datesFilters.toDateCached = undefined;
    this.getOrders(0);
  }

  public removeWithoutDueDateFilter() {
    this.datesFilters.notSetDueDate = false;
    this.getOrders(0);
  }

  public onInfo(item: OrderListItem) {
    const params: Params = { orderId: item.orderId, orderNumber: item.orderNumber };
    this.sidenavService.newItemUrlPart('order-list-auto-sorting', params);
    this.orderId = item.orderId;
    this.orderNumber = item.orderNumber;
    this.orders.data = undefined;
  }

  public onExitFromInfo() {
    const params: Params = {};
    this.sidenavService.newItemUrlPart('order-list-auto-sorting', params);
    this.orderId = null;
    this.orderNumber = null;
    this.getOrders(0);
  }

  public getOrders(page: number) {
    this.orders.page = page;
    const filters: OrdersListFilters = {
      notSetDueDate: !!this.datesFilters.notSetDueDate,
      photographerIds: [],
      fromDueDate: this.datesFilters.fromDateCached ? parseZone(this.datesFilters.fromDateCached).utc(true).toISOString() : '',
      toDueDate: this.datesFilters.toDateCached ? parseZone(this.datesFilters.toDateCached).utc(true).toISOString() : '',
      orderStatusIds: this.statusFilters.selectedCached.map(x => new Ids(x.orderStatusId))
    };
    const variables = {
      photographerId: +this.#appService.user.userId,
      paginationPage: this.orders.page,
      paginationSize: this.orders.size,
      search: this.orders.search ?? '',
      onGoing: false,
      filters
    }

    this.#graphQLService
      .getData(PhotographerQueries.GET_PHOTOGRAPHER_ORDERS, variables)
      .pipe(takeUntilDestroyed(this.#destroyRef), map((res: any) => res['data']['photographerOrders']['nodes'][0]))
      .subscribe({
        next: (data) => {
          this.orders.data = UtilityFunctions.DeepClone(data.orders);
          this.orders.total = UtilityFunctions.DeepClone(data.total);
        }
      });
  }

  private searchTextSubscription() {
    this.subscription.add(this.topbarService.searchTextSubscribe.subscribe(data => {
      if (data !== this.orders.search && !this.orderId && !this.orderNumber) {
        this.orders.search = data;
        this.getOrders(0);
      }
    }));
  }

  private setGridColumns() {
    this.gridColumns = [
      { title: '', field: '', type: GridColumnTypeEnum.icon, width: 20 },
      { title: 'Order #', field: 'orderNumber' },
      { title: 'Status', field: 'orderStatusName' },
      { title: 'Due Date', field: 'dueDate', type: GridColumnTypeEnum.date },
      { title: 'Shots', field: '', type: GridColumnTypeEnum.custom }
    ];
  }

  private queryParamsSubscribe() {
    this.subscription.add(this._activatedRoute.queryParams.subscribe(data => {
      const orderId = data['orderId'];
      this.orderId = !!orderId ? +orderId : null;
      const orderNumber = data['orderNumber'];
      this.orderNumber = orderNumber || null;
      const modelId = data['modelId'];
      this.modelId = !!modelId ? +modelId : null;
      this.orders.search = null;
      if (!this.orderId || !this.orderNumber) {
        this.orderId = null;
        this.orderNumber = null;
        this.getOrders(0);
      } else {
        this.orders.data = null;
      }
    }));
  }
}
